import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Badge } from '../components/ui';
import RevenueCard from '../components/RevenueCard';
import AccountsCard from '../components/AccountsCard';
import BusinessAreaCard from '../components/BusinessAreaCard';
import {
  Task, TaskStatus, TaskPriority,
  Project, Client,
} from '../types';
import taskService from '../services/taskService';
import projectService from '../services/projectService';
import clientService from '../services/clientService';

/* ============================================================
   Status / Priority visuals
   ============================================================ */

const statusVariant = (s: TaskStatus): 'purple' | 'amber' => (
  s === TaskStatus.TODO ? 'purple' : 'amber'
);

const statusLabel = (s: TaskStatus): string => (
  s === TaskStatus.TODO ? 'Offen' : 'In Arbeit'
);

const priorityVariant = (p: TaskPriority): 'red' | 'amber' | 'green' => {
  switch (p) {
    case TaskPriority.HIGH: return 'red';
    case TaskPriority.MEDIUM: return 'amber';
    case TaskPriority.LOW: return 'green';
  }
};

const priorityLabel = (p: TaskPriority): string => {
  switch (p) {
    case TaskPriority.HIGH: return 'Hoch';
    case TaskPriority.MEDIUM: return 'Mittel';
    case TaskPriority.LOW: return 'Niedrig';
  }
};

/** Sort: HIGH before MEDIUM before LOW; within priority, TODO before IN_PROGRESS; then position. */
const taskOrder = (a: Task, b: Task): number => {
  const prioRank: Record<TaskPriority, number> = {
    [TaskPriority.HIGH]: 0, [TaskPriority.MEDIUM]: 1, [TaskPriority.LOW]: 2,
  };
  const pr = prioRank[a.priority] - prioRank[b.priority];
  if (pr !== 0) return pr;
  const statusRank: Record<TaskStatus, number> = {
    [TaskStatus.TODO]: 0, [TaskStatus.IN_PROGRESS]: 1, [TaskStatus.DONE]: 2,
  };
  const sr = statusRank[a.status] - statusRank[b.status];
  if (sr !== 0) return sr;
  return a.position - b.position;
};

/* ============================================================
   Main component
   ============================================================ */

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  usePageTitle('Dashboard');

  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, p, c] = await Promise.all([
          taskService.getAll([TaskStatus.TODO, TaskStatus.IN_PROGRESS]),
          projectService.getAll(),
          clientService.getAll(),
        ]);
        setTasks(t);
        setProjects(p);
        setClients(c);
      } catch (err) {
        console.error(err);
        toast.error('Konnte Aufgaben nicht laden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const projectById = useMemo(() => {
    const m = new Map<number, Project>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const clientById = useMemo(() => {
    const m = new Map<number, Client>();
    for (const c of clients) m.set(c.id, c);
    return m;
  }, [clients]);

  /** Group tasks by projectId; order groups by client name then project name. */
  const grouped = useMemo(() => {
    const byProject = new Map<number, Task[]>();
    for (const t of tasks) {
      const list = byProject.get(t.projectId) ?? [];
      list.push(t);
      byProject.set(t.projectId, list);
    }
    const groups = Array.from(byProject.entries()).map(([projectId, list]) => {
      const project = projectById.get(projectId);
      const client = project ? clientById.get(project.clientId) : undefined;
      return {
        projectId,
        project,
        client,
        tasks: list.sort(taskOrder),
      };
    });
    const prioRank: Record<TaskPriority, number> = {
      [TaskPriority.HIGH]: 0, [TaskPriority.MEDIUM]: 1, [TaskPriority.LOW]: 2,
    };
    const topPrio = (ts: Task[]): number =>
      ts.reduce((min, t) => Math.min(min, prioRank[t.priority]), 3);
    groups.sort((a, b) => {
      const pr = topPrio(a.tasks) - topPrio(b.tasks);
      if (pr !== 0) return pr;
      const ac = a.client?.name ?? '';
      const bc = b.client?.name ?? '';
      if (ac !== bc) return ac.localeCompare(bc);
      return (a.project?.name ?? '').localeCompare(b.project?.name ?? '');
    });
    return groups;
  }, [tasks, projectById, clientById]);

  const todoCount = tasks.filter((t) => t.status === TaskStatus.TODO).length;
  const inProgressCount = tasks.filter((t) => t.status === TaskStatus.IN_PROGRESS).length;
  const highCount = tasks.filter((t) => t.priority === TaskPriority.HIGH).length;

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 5) return 'Burning the midnight oil';
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    if (hour < 22) return 'Good evening';
    return 'Working late';
  };

  return (
    <div className="p-6 lg:p-10">
      {/* Hero welcome */}
      <div className="mb-8 animate-fade-up">
        <p
          className="text-sm font-medium uppercase tracking-widest mb-2"
          style={{ color: 'var(--accent-warm)', letterSpacing: '0.1em' }}
        >
          {greeting()}
        </p>
        <h1
          className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight"
          style={{ color: 'var(--text-primary)', letterSpacing: '-0.025em' }}
        >
          {user?.firstName}
        </h1>
        <p className="mt-2 text-lg" style={{ color: 'var(--text-muted)' }}>
          Deine offenen Aufgaben über alle Kunden und Projekte.
        </p>
      </div>

      {/* Umsatz aus den gestellten Rechnungen, dazu die Prognose aus den Serien */}
      <RevenueCard />

      {/* Kontostände; blendet sich aus, solange kein Konto angelegt ist */}
      <AccountsCard />

      {/* Steht unter den Konten, weil es beide Seiten zusammenfasst — und weil es erst etwas
          zeigt, wenn Rechnungen und Ausgaben zugeordnet sind. */}
      <BusinessAreaCard />

      {/* Stats strip */}
      <div className="flex items-baseline gap-5 nums-tabular mb-6 animate-fade-up">
        <Stat label="Offen" value={todoCount} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>·</span>
        <Stat label="In Arbeit" value={inProgressCount} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>·</span>
        <Stat label="Hohe Priorität" value={highCount} />
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3 animate-fade-up">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="rounded-xl px-5 py-4"
              style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}
            >
              <div className="skeleton h-3 w-40 mb-3"></div>
              <div className="skeleton h-4 w-2/3 mb-2"></div>
              <div className="skeleton h-4 w-1/2"></div>
            </div>
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3 animate-fade-up">
          {grouped.map((g) => (
            <ProjectGroup
              key={g.projectId}
              projectId={g.projectId}
              projectName={g.project?.name ?? 'Projekt #' + g.projectId}
              clientName={g.client?.name ?? '—'}
              tasks={g.tasks}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   Stat — label + tabular number
   ============================================================ */

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <span className="flex items-baseline gap-1.5">
    <span
      className="text-xl font-extrabold tracking-tight"
      style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
    >
      {value}
    </span>
    <span
      className="text-[11px] font-semibold uppercase tracking-widest"
      style={{ color: 'var(--text-muted)' }}
    >
      {label}
    </span>
  </span>
);

/* ============================================================
   ProjectGroup — card per project with task rows
   ============================================================ */

const ProjectGroup: React.FC<{
  projectId: number;
  projectName: string;
  clientName: string;
  tasks: Task[];
}> = ({ projectId, projectName, clientName, tasks }) => (
  <div
    className="group/card rounded-xl overflow-hidden"
    style={{
      background: 'var(--surface-bg)',
      border: '1px solid var(--surface-border)',
      boxShadow: 'var(--surface-shadow)',
    }}
  >
    {/* Header: Kunde · Projekt — opens the board */}
    <Link
      to={`/projects/${projectId}`}
      className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 transition-colors"
      style={{ borderBottom: '1px solid var(--surface-border)', textDecoration: 'none' }}
      title="Board öffnen"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--table-header-bg)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <span
          className="text-[11px] font-bold uppercase tracking-[0.16em] truncate"
          style={{ color: 'var(--text-muted)' }}
        >
          {clientName}
        </span>
        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>·</span>
        <span
          className="text-sm font-bold tracking-tight truncate"
          style={{ color: 'var(--text-primary)' }}
        >
          {projectName}
        </span>
      </div>
      <span
        className="text-[11px] font-semibold uppercase tracking-wider shrink-0"
        style={{ color: 'var(--text-muted)' }}
      >
        {tasks.length} {tasks.length === 1 ? 'Aufgabe' : 'Aufgaben'}
      </span>
    </Link>

    {/* Task rows */}
    <div>
      {tasks.map((t, i) => (
        <TaskRow key={t.id} task={t} projectId={projectId} isFirst={i === 0} />
      ))}
    </div>
  </div>
);

/* ============================================================
   TaskRow — single task, full row clickable → opens board
   ============================================================ */

const TaskRow: React.FC<{ task: Task; projectId: number; isFirst: boolean }> = ({
  task, projectId, isFirst,
}) => (
  <div
    className="group/row relative grid items-center gap-3 px-4 sm:px-5 py-2.5 transition-colors"
    style={{
      gridTemplateColumns: 'auto minmax(140px,1fr) auto auto auto',
      borderTop: isFirst ? 'none' : '1px solid var(--surface-border)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--table-row-hover)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    <Link
      to={`/projects/${projectId}`}
      aria-label={`Board zu "${task.title}" öffnen`}
      title="Board öffnen"
      className="absolute inset-0 z-0"
    />

    {/* Priority dot */}
    <span
      className="relative z-[1] inline-block h-2 w-2 rounded-full pointer-events-none"
      style={{ background: `var(--badge-${priorityVariant(task.priority)}-text)` }}
      aria-hidden="true"
    />

    <span
      className="relative z-[1] text-[13px] font-semibold tracking-tight truncate pointer-events-none"
      style={{ color: 'var(--text-primary)' }}
    >
      {task.title}
    </span>

    <div className="relative z-[1] flex items-center gap-1.5 pointer-events-none">
      <Badge variant={statusVariant(task.status)}>{statusLabel(task.status)}</Badge>
    </div>

    <div className="relative z-[1] flex items-center gap-1.5 pointer-events-none">
      <Badge variant={priorityVariant(task.priority)}>{priorityLabel(task.priority)}</Badge>
    </div>

    <div
      className="relative z-[1] pointer-events-none transition-transform group-hover/row:translate-x-0.5"
      style={{ color: 'var(--text-muted)' }}
      aria-hidden="true"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
      </svg>
    </div>
  </div>
);

/* ============================================================
   Empty state
   ============================================================ */

const EmptyState: React.FC = () => (
  <div
    className="rounded-2xl py-16 px-6 text-center animate-fade-up"
    style={{
      background: 'var(--surface-bg)',
      border: '1px dashed var(--surface-border)',
    }}
  >
    <div
      className="mx-auto mb-5 h-16 w-16 rounded-2xl flex items-center justify-center"
      style={{ background: 'var(--table-header-bg)', color: 'var(--text-muted)' }}
    >
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <h3 className="text-lg font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
      Keine offenen Aufgaben
    </h3>
    <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
      Alle Boards sind aufgeräumt. Gute Arbeit.
    </p>
  </div>
);

export default Dashboard;
